/**
 * `<EditorMetaSidecar>` — the Editor's right pane (UX-reconciliation F4.1; the
 * standalone's three-pane composition's metadata sidecar).
 *
 * Six sections (matching the standalone): Outline · Tags · Action items · Entities ·
 * Backlinks · Details. Composition only — every data point comes from a hook that
 * already exists:
 *
 *   - **Outline** — computed from the live editor body via `extractOutline()`.
 *   - **Tags** — `companion.autoHashtags` from `useNoteQuery(noteId, true)`.
 *   - **Action items** — `companion.actionItems` from the same query.
 *   - **Entities** — `companion.entities` from the same query.
 *   - **Backlinks** — `getBacklinks` tool wrapper (existing api/tools.ts).
 *   - **Details** — pulls from the `note` and `companion` fields.
 *
 * No new backend touchpoints — the only F4.1 backend addition is the Notebooks tree
 * endpoint, scoped to `<NotebooksTreePane>` (F1 docket).
 */

import './richTextEditor.css';

import { memo, useMemo, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Icon } from '@/components';
import { getBacklinks, callEndpoint } from '@/api';
import { useNoteQuery } from '@/hooks/useNoteQuery';
import { useListNotebooksQuery, resolveNotebookPath } from '@/hooks/useNotebookQueries';
import { useUiStore } from '@/stores';
import type {
  ActionItemView,
  CompanionAutoHashtag,
  CompanionEntity,
  LinkedNoteList,
  NoteId,
  NoteView,
} from '@/types';
import { extractOutline, type OutlineEntry } from './outline';
import { NoteIdRow } from './NoteIdRow';
import { EditorActionItems } from './EditorActionItems';

/**
 * `<CollapsibleMetaSection>` — a local replacement for `<MetaSection>` that
 * adds a collapse/expand toggle on the header (Task 6, 2026-05-21).
 *
 * MetaSection lives in `@/components` and is owned by other surfaces, so rather
 * than change its contract we render the same `.meta-section` markup here with
 * a clickable header. Collapse state persists in `useUiStore` via
 * `togglePanelCollapsed(sectionId)` / `isPanelCollapsed(sectionId)` keyed by a
 * stable per-section id. Collapsed = the body is omitted, only the header shows.
 */
function CollapsibleMetaSection({
  sectionId,
  title,
  count,
  children,
  defaultCollapsed = false,
}: {
  sectionId: string;
  title: string;
  count?: number | null;
  children: ReactNode;
  /** Collapsed-by-default when the user has never toggled this section. */
  defaultCollapsed?: boolean;
}): JSX.Element {
  const collapsed = useUiStore((s) => s.isPanelCollapsed(sectionId, defaultCollapsed));
  const toggle = useUiStore((s) => s.togglePanelCollapsed);
  return (
    <div className="meta-section">
      <button
        type="button"
        className="meta-section__head meta-section__head--toggle"
        aria-expanded={!collapsed}
        onClick={() => toggle(sectionId, defaultCollapsed)}
      >
        <Icon
          name={collapsed ? 'chevron_right' : 'chevron_down'}
          size={12}
        />
        <span className="label">
          {title}
          {count !== null && count !== undefined && (
            // U7: "· 36", not "·36" — the glued middot read as a minus sign
            // ("ENTITIES −36") in the cross-theme design tour.
            <span className="label-count">· {count}</span>
          )}
        </span>
      </button>
      {!collapsed && <div className="meta-section__body">{children}</div>}
    </div>
  );
}

interface EditorMetaSidecarProps {
  /** The current note id — `null` for an unsaved scratch buffer (the sidecar renders empty). */
  noteId: NoteId | null;
  /** The live editor body — used to compute the Outline on every change. */
  body: string;
  /** Click handler for outline rows — owner scrolls the editor (Tiptap and/or CM6)
   *  to the chosen heading. Optional: when absent, outline rows render as static text
   *  (the prior behaviour, no interaction). */
  onJumpToHeading?: (entry: OutlineEntry) => void;
}

function EditorMetaSidecarImpl({
  noteId,
  body,
  onJumpToHeading,
}: EditorMetaSidecarProps): JSX.Element {
  // Full note + companion — the editor's main query uses `includeCompanion: false`;
  // the sidecar wants the companion (tags / entities / action items). Separate cache
  // key, so the editor's body fetch and this sidecar fetch are independent — the body
  // renders immediately and the sidecar progressively fills.
  const noteQuery = useNoteQuery(noteId, true);
  const note: NoteView | undefined = noteQuery.data;
  // The note's notebook directory path (readable), so the Details row never shows a raw UUID.
  const notebooksList = useListNotebooksQuery();
  const notebookPath = resolveNotebookPath(
    note?.note.notebookId ?? null,
    notebooksList.data?.items ?? [],
  );
  const notebookPathLabel = notebookPath.length > 0 ? notebookPath.join(' / ') : '—';

  const qc = useQueryClient();
  // Remove an entity FROM this note (the note↔entity link). The canonical entity is
  // preserved; only this note's mention is dropped. Refreshes the note query so the pane
  // updates. Errors are swallowed to a console warning (the row simply stays).
  const onRemoveEntity = (entityId: string): void => {
    if (noteId === null) return;
    void (async () => {
      try {
        await callEndpoint(
          'DELETE',
          `notes/${encodeURIComponent(noteId)}/entities/${encodeURIComponent(entityId)}`,
        );
        void qc.invalidateQueries({ queryKey: ['note', noteId] });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[editor] remove entity failed', err);
      }
    })();
  };

  // Backlinks — separate query (the `get_backlinks` tool isn't part of `get_note`).
  const backlinksQuery = useQuery({
    queryKey: ['backlinks', noteId ?? '__none__'],
    enabled: noteId !== null,
    queryFn: async () => {
      const result = await getBacklinks({ id: noteId as NoteId, limit: 50 });
      return result.data as LinkedNoteList;
    },
  });

  // Outline — pure transform from the live body, memoized so a re-render without a
  // body change skips the work.
  const outline: OutlineEntry[] = useMemo(() => extractOutline(body), [body]);

  // v4 refit (2026-05-18, GAP_ANALYSIS.md §E5):
  //
  // The apologetic noteId-null early-return ("Save the note to see its tags,
  // entities, backlinks, and details.") is gone. Per the design spec the rail
  // must ALWAYS show its structure — section headers with ·0 counts and a
  // one-line empty hint inside each. "The skeleton is the message." Empty
  // hints use the "they'll appear as you …" pattern from the gap analysis.
  //
  // The Details section is the only exception — for an unsaved scratch note
  // there's nothing to show (no created/updated/source yet), so it's hidden
  // entirely rather than padded with em-dashes that would suggest fields that
  // exist-but-are-empty. The four load-bearing sections (Outline, Action items,
  // Entities, Backlinks) and the Tags chip strip render in every state.
  const tags: CompanionAutoHashtag[] = note?.companion?.autoHashtags ?? [];
  const actionItems: ActionItemView[] = note?.companion?.actionItems ?? [];
  const entities: CompanionEntity[] = note?.companion?.entities ?? [];
  const backlinks = backlinksQuery.data?.items ?? [];

  return (
    <aside className="meta-sidecar" aria-label="Note metadata">
      {/* v4 P3 (GAP_ANALYSIS.md §E8): copyable note-uuid row at the very top
          of the rail. Useful for ops + support flows. Renders nothing for an
          unsaved scratch buffer (no id yet). */}
      <NoteIdRow noteId={noteId} />

      <CollapsibleMetaSection
        sectionId="editor.meta.outline"
        title="Outline"
        count={outline.length}
      >
        {outline.length === 0 ? (
          <div className="meta-sidecar__empty">
            No headings yet — they'll appear as you add # H1 / ## H2.
          </div>
        ) : (
          <div className="meta-sidecar__outline">
            {outline.map((o, i) => (
              <button
                key={`${o.line}:${i}`}
                type="button"
                className="meta-sidecar__outline-row"
                style={{ paddingLeft: 4 + (o.level - 1) * 12 }}
                title={`Jump to "${o.text}" (line ${o.line})`}
                onClick={() => onJumpToHeading?.(o)}
                disabled={onJumpToHeading === undefined}
              >
                {o.text}
              </button>
            ))}
          </div>
        )}
      </CollapsibleMetaSection>

      <CollapsibleMetaSection
        sectionId="editor.meta.tags"
        title="Tags"
        count={tags.length}
        defaultCollapsed
      >
        {tags.length === 0 ? (
          <div className="meta-sidecar__empty">
            No tags yet — they'll appear after extraction.
          </div>
        ) : (
          <div className="meta-sidecar__tags">
            {tags.map((t) => (
              <span key={t.tagPath} className="meta-sidecar__tag">
                #{t.tagPath}
              </span>
            ))}
          </div>
        )}
      </CollapsibleMetaSection>

      <CollapsibleMetaSection
        sectionId="editor.meta.actionItems"
        title="Action items"
        count={actionItems.length}
      >
        <EditorActionItems noteId={noteId} actionItems={actionItems} />
      </CollapsibleMetaSection>

      <CollapsibleMetaSection
        sectionId="editor.meta.entities"
        title="Entities"
        count={entities.length}
      >
        {entities.length === 0 ? (
          <div className="meta-sidecar__empty">
            No entities yet — they'll appear as you write.
          </div>
        ) : (
          <div className="meta-sidecar__entities">
            {entities.map((e) => (
              <div key={e.entityId} className="meta-sidecar__entity-row">
                <span
                  className={'meta-sidecar__entity-type meta-sidecar__entity-type--' + e.type}
                >
                  {e.type.slice(0, 2).toUpperCase()}
                </span>
                <span className="meta-sidecar__entity-name">{e.canonical}</span>
                <span className="meta-sidecar__entity-count">{e.mentionCount}</span>
                {/* Per-entity remove — drops the note↔entity link via
                    DELETE /api/v1/notes/{id}/entities/{id} (the canonical
                    entity is preserved for other notes). */}
                <button
                  type="button"
                  className="meta-sidecar__entity-remove"
                  onClick={() => onRemoveEntity(e.entityId)}
                  title={`Remove ${e.canonical} from this note`}
                  aria-label={`Remove ${e.canonical} from this note`}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
      </CollapsibleMetaSection>

      <CollapsibleMetaSection
        sectionId="editor.meta.backlinks"
        title="Backlinks"
        count={backlinks.length}
      >
        {backlinks.length === 0 ? (
          <div className="meta-sidecar__empty">
            No backlinks yet — they'll appear as other notes link here.
          </div>
        ) : (
          <div className="meta-sidecar__backlinks">
            {backlinks.slice(0, 20).map((b) => (
              <a key={b.noteId} className="meta-sidecar__backlink" title={b.title}>
                {b.title}
              </a>
            ))}
            {backlinks.length > 20 && (
              <div className="meta-sidecar__backlink-more">
                + {backlinks.length - 20} more
              </div>
            )}
          </div>
        )}
      </CollapsibleMetaSection>

      {/* Details only render for a saved note — there's nothing meaningful to
          show for an unsaved scratch buffer. See header docstring. */}
      {noteId !== null && (
        <CollapsibleMetaSection
          sectionId="editor.meta.details"
          title="Details"
          count={null}
        >
          {note === undefined ? (
            <div className="meta-sidecar__empty">Loading…</div>
          ) : (
            <div className="meta-sidecar__details">
              <span>Source</span>
              <span>
                <span className="meta-sidecar__chip">{note.note.source}</span>
              </span>
              <span>Created</span>
              <span>{formatDate(note.note.createdAt)}</span>
              <span>Updated</span>
              <span>{formatDate(note.note.updatedAt)}</span>
              <span>Words</span>
              <span>{wordCount(note.body).toLocaleString()}</span>
              {/* v4 P10 (2026-05-18, GAP_ANALYSIS_R2.md §E12): Tokens row
                  alongside Words. Sums companion.chunks[].tokenCount when the
                  companion is present + populated; otherwise em-dash so the
                  row's structure stays visible while extraction is pending. */}
              <span>Tokens</span>
              <span>{formatTokens(note.companion?.chunks)}</span>
              <span>Notebook</span>
              <span title={notebookPathLabel}>{notebookPathLabel}</span>
            </div>
          )}
        </CollapsibleMetaSection>
      )}
    </aside>
  );
}

// Memoized so EditorSurface's frequent unrelated re-renders (save-state ticks, view-mode,
// hover, zoom/margin sliders) don't re-render the whole sidecar — it only re-renders when
// noteId / body (debounced) / onJumpToHeading actually change (perf, 2026-06-10).
export const EditorMetaSidecar = memo(EditorMetaSidecarImpl);

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return iso;
  }
}

function wordCount(body: string): number {
  if (body.length === 0) return 0;
  return body.split(/\s+/).filter((w) => w.length > 0).length;
}

/**
 * v4 P10 (GAP_ANALYSIS_R2.md §E12): sum chunk-level token counts for the
 * Tokens row. ``tokenCount`` is `number | null` per ChunkView — a null on
 * any chunk means the count isn't known yet (pre-A2 / pre-tokenizer-pass);
 * we sum what's known and only fall back to em-dash if NO chunk has a count.
 */
function formatTokens(
  chunks: { tokenCount: number | null }[] | undefined,
): string {
  if (chunks === undefined || chunks.length === 0) return '—';
  let total = 0;
  let known = 0;
  for (const c of chunks) {
    if (c.tokenCount !== null) {
      total += c.tokenCount;
      known += 1;
    }
  }
  if (known === 0) return '—';
  return total.toLocaleString();
}
